import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { ControllerInterface } from "csi/action/Controller";
import CsiFormData from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import def from "csi/def";
import { Context } from "csi/def/Context";
import { View } from "csi/def/View";
import { FormWidget } from "csi/form/Widget";
import { ControllerSaveFunction, storeFormDataFieldValue } from "csi/middleware/common";
import reflection from "csi/reflection";
import { submitWriter, writeFormFields } from "csi/service/writerService";
import WorkingCache from "csi/state/WorkingCache";
import { TableWidget } from "csi/table/Widget";
import { ATTR_NAME, C8Node, cnl, ICache } from "csi/typings";
import csiView from "csi/view";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import { green, red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";

export const TYPE_HIGG_CATEGORY = "HiggCategory";
export const TYPE_HIGG_MATERIAL = "HiggMaterial";
export const TYPE_HIGG_PROCESS = "HiggProcess";
export const TYPE_HIGG_MATERIAL_COMPOSITION = "HiggMaterialComposition";
const URL_PREFIX_MATERIAL = "centric://HIGG_MATERIAL/";

interface Score {
	abioticDepletion: number;
	abioticDepletionPts: number;
	biogenicCarbon: number;
	chemistry: number;
	chemistryPts: number;
	eutrophication: number;
	eutrophicationPts: number;
	globalWarming: number;
	globalWarmingPts: number;
	waterConsumption: number;
	waterScarcity: number;
	waterScarcityPts: number;
}

interface HiggResponse {
	_id: string;
	errorMessage?: string;
	scores?: Score | null;
};

/** create higg material composition key */
export function createCompositionKey(baseHiggMaterial: cnl, currentHiggMaterialId?: string, processId?: string): string {
	if (currentHiggMaterialId && processId) {
		return `${baseHiggMaterial}/${currentHiggMaterialId}/${processId}`;
	} else if (currentHiggMaterialId) {
		return `${baseHiggMaterial}/${currentHiggMaterialId}`;
	}
	return `${baseHiggMaterial}`;
}

export class HiggMaterialComposition {
	@observable private _blendPercentage: number = 0;
	@observable private _process: ComponentItem = null;
	@observable private _key: string;
	@observable.ref private _processItems: ComponentItem[] = null;

	/** base material */
	readonly baseHiggMaterial: cnl;

	/** working cache */
	readonly cache: ICache;

	/** indicate if in the case of edit */
	readonly isEdit: boolean;

	/** percentage of blend , range is 1 to 100 */
	get blendPercentage(): number {
		return this._blendPercentage;
	}

	/** the percentage would go to C8 database, range is 0.1 - 1  */
	get c8BlendPercentage(): number {
		return this.blendPercentage / 100;
	}

	get compositionKey(): string {
		return this._key;
	}

	/** composition node, which equals base higg material  */
	@computed get compositionNode(): C8Node {
		const { cache, baseHiggMaterial } = this;
		return cache.get(baseHiggMaterial);
	}

	/** default process */
	@computed get defaultProcess(): ComponentItem | undefined {
		const { processItems } = this;
		return processItems?.find(({ value }) => value === this.compositionNode.DefaultProcess);
	}

	/** indicate if percentage is valid or not */
	get percentageIsInvalid(): boolean {
		const { blendPercentage } = this;
		return blendPercentage <= 0 || blendPercentage > 100;
	}

	/** process of this composition */
	get process(): ComponentItem {
		return this._process;
	}

	/** options for process */
	get processItems(): ComponentItem[] {
		return this._processItems;
	}

	/** fetch processes based on base material */
	@action fetchProcesses = async (): Promise<void> => {
		const { baseHiggMaterial } = this;
		const search = new Search().byType(TYPE_HIGG_PROCESS);

		search.getQuery()
			.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, baseHiggMaterial);

		const response = await search.execute();

		runInAction(() => {
			this._processItems = response.getNodes().map(({ Code, $Name, $URL }) => ({ name: $Name, id: Code, value: $URL } as ComponentItem));
		});
	};

	/** set percentage */
	@action setBlendPercentage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		let amount = parseInt(event.target.value);

		if (isNaN(amount)) {
			amount = 0;
		}

		this._blendPercentage = amount;
	};

	/** set process */
	@action setProcess = (event: ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason): void => {
		this._process = value;
	};

	constructor(baseHiggMaterial: cnl, cache: ICache, isEdit: boolean = false, compositionKey: string, blendPercentage?: number, process?: ComponentItem) {
		makeObservable(this);
		this.baseHiggMaterial = baseHiggMaterial;
		this.cache = cache;
		this.isEdit = isEdit;
		this._key = compositionKey;
		this._blendPercentage = blendPercentage || 0;
		this._process = process;
	}
}

export class HiggMaterialStore extends WizardStore {
	/** action controller to access to be able to change writer */
	readonly actionController: ControllerInterface;

	/** the composition map */
	@observable compositionMap: Record<number, HiggMaterialComposition> = {};
	@observable private _nameViolationRes: string = "";

	/** FormData for the wizard */
	readonly formData: CsiFormData;

	private readonly cache: ICache = new WorkingCache();

	/** evaluate scores */
	private higgResponse: HiggResponse;

	/** indicate if it's the first initialization in the case of edit during user switch the pages back and forth */
	private _firstInitializationToEdit = true;

	/** gets the current base material library */
	get baseHiggMaterialLibrary(): Set<cnl> {
		const { compositionMap } = this;
		return new Set(Object.values(compositionMap).map((x) => x.baseHiggMaterial));
	}

	/** all the compositions */
	@computed get compositions(): HiggMaterialComposition[] {
		const { compositionMap } = this;
		return Object.values(compositionMap);
	}

	/** count of compositions */
	get count(): number {
		return this.indexes.length;
	}

	/** the current company custom material in the case of edit */
	get currentHiggMaterial(): C8Node | null {
		const { formData } = this;
		return formData.getNode();
	}

	/** the index list of all the compositions */
	@computed get indexes(): number[] {
		return Object.keys(this.compositionMap).map((index) => parseInt(index));
	}

	/** parameter - "defView" to populate the dojo view */
	@computed get defExampleMaterialView(): View {
		const { formData, onSelectHiggMaterial } = this;

		// TODO: use FormData.addSelectListener?
		formData.onSelect = onSelectHiggMaterial;

		const rc = reflection.getClass(TYPE_HIGG_MATERIAL);
		const defView = def.getClassViewDef(rc, "HiggExampleMaterialLib") as View;
		defView.setFormData(formData);

		return defView;
	}

	/** indicate if in the case of edit higg material */
	get editHigg(): boolean {
		const { currentHiggMaterial } = this;

		return !!currentHiggMaterial;
	}

	/** if limit is reached return satisfied if not, then return unsatisfied */
	get higgCompositionGuidanceSatisfied(): string {
		if (!this.compositions.length) {
			return "HiggWizardSelectionEmptyError";
		}
		if (!this.allPercentagesPositive) {
			return "HiggCompositionGuidanceSingleNotSatisfied";
		}
		if (!this.reachLimit) {
			return "HiggCompositionGuidanceNotSatisfied";
		}
		return "HiggCompositionGuidanceSatisfied";
	}

	@computed get hint(): { color: string } {
		return {
			color: this.higgCompositionGuidanceSatisfied !== "HiggCompositionGuidanceSatisfied" ? red[600] : green[600],
		};
	};

	/** localization for name violation*/
	get nameViolationRes(): string {
		return this._nameViolationRes;
	}

	/** indicate if percentage reaches 100% */
	@computed get reachLimit(): boolean {
		const { compositions } = this;
		let amount = 0;

		compositions.forEach(({ blendPercentage }) => {
			amount += blendPercentage;
		});

		return (amount == 100);
	}

	get allPercentagesPositive(): boolean {
		let allPositive = true;
		this.compositions.forEach((composition) => {
			if (composition.blendPercentage <= 0) {
				allPositive = false;
			}
		});
		return allPositive;
	}

	/** does the composition map have a certain base material */
	private _compositionsHasBaseMaterial = (baseHiggMaterial: string): boolean => {
		const { baseHiggMaterialLibrary } = this;
		return baseHiggMaterialLibrary.has(baseHiggMaterial);
	};

	//#region override functions 

	/** extract information from form */
	protected onNextPage = async (index: number): Promise<boolean> => {
		if (index == 0) {
			const { addComposition, cache, currentHiggMaterial, editHigg, formData, validateHiggMaterialName } = this;
			const formWidget = csiView.getView(def.DEFAULT_FORM_ID) as FormWidget;

			if (!formWidget.validateForm({ message: "" })) {
				// TODO - how to render the error message
				return false;
			}

			// validate the higg material name in the case of creation
			if (!editHigg) {
				const name = formWidget.getFieldValue(ATTR_NAME);
				const result = await validateHiggMaterialName(name);

				if (!result) {
					return false;
				}
			}

			// store field values as initial values in form data - in case Back button is pressed and need to rebuild form,
			// or in case Finish button is pressed and need to add form values to request
			formData.getFields().forEach((f) => {
				const value = f.getValue();
				let localizedValue = f.getFormattedValue();

				if (xml.isValidURL(value)) {
					const category = cache.get(value) || formWidget.cacheView.get(value);

					// save to cache so that we can use it in the next step
					cache.add(category);

					localizedValue = this.locale.getNodeName(category);
				}

				const isRequired = f.m_bRequired;

				// only add the non empty values of required fields
				if (isRequired && value) {
					this.stepInfoList[0].values.push(localizedValue);
				}

				// populate the initial value to save the information to node later
				storeFormDataFieldValue(f, formData);
			});

			// initialize the compositions in the case of edit, only do so for the first initialization, after that, will treat it as the creation case to avoid the inconsistencies
			// The reason is that user could made any changes to compositions and then go back to the first page, then go to the second page
			if (editHigg && this._firstInitializationToEdit) {
				const compositionNodes = currentHiggMaterial.HiggMaterialCompositions.map((cnl) => cache.get(cnl));

				runInAction(() => {
					compositionNodes.forEach(({ BaseHiggMaterial, BlendPercentage, Process }) => {
						const processNode = cache.get(Process);

						// add to the right pane
						addComposition(BaseHiggMaterial, cache, true, BlendPercentage * 100, processNode ? { name: processNode.$Name, id: processNode.Code, value: processNode.$URL } : null);

						// add to the aggregated list
						const baseMaterialNode = cache.get(BaseHiggMaterial);
						formData.addAggregatedNode(baseMaterialNode);
					});
				});

				this._firstInitializationToEdit = false;
			}
		}

		return true;
	};

	/** save Higg Material */
	protected onSavePromise = async (): Promise<JsonResponse> => {
		const { actionController, currentHiggMaterial, editHigg, compositions, higgResponse } = this;
		const { m_writer: w, m_formData: fd } = actionController;
		const { scores, _id } = higgResponse;

		// extract scores
		const {
			abioticDepletion,
			abioticDepletionPts,
			biogenicCarbon,
			chemistry,
			chemistryPts,
			eutrophication,
			eutrophicationPts,
			globalWarming,
			globalWarmingPts,
			waterConsumption,
			waterScarcity,
			waterScarcityPts,
		} = scores;

		const currentHiggMaterialUrl = currentHiggMaterial?.$URL;

		// save higg material node
		const higgMaterialUrl = currentHiggMaterialUrl || `${URL_PREFIX_MATERIAL}${_id}`;

		const cn = w.createNode(higgMaterialUrl, TYPE_HIGG_MATERIAL, null, null, true);

		// populate scores
		cn.changeAttribute("Id", xml.ATTR_STRING, _id)
			.changeAttribute("HiggMaterialClass", xml.ATTR_ENUM, "HiggMaterialClasses:Company Custom")
			.changeAttribute("AbioticDepletion", xml.ATTR_DOUBLE, abioticDepletion)
			.changeAttribute("AbioticDepletionPts", xml.ATTR_DOUBLE, abioticDepletionPts)
			.changeAttribute("BiogenicCarbon", xml.ATTR_DOUBLE, biogenicCarbon)
			.changeAttribute("Chemistry", xml.ATTR_DOUBLE, chemistry)
			.changeAttribute("ChemistryPts", xml.ATTR_DOUBLE, chemistryPts)
			.changeAttribute("Eutrophication", xml.ATTR_DOUBLE, eutrophication)
			.changeAttribute("EutrophicationPts", xml.ATTR_DOUBLE, eutrophicationPts)
			.changeAttribute("GlobalWarming", xml.ATTR_DOUBLE, globalWarming)
			.changeAttribute("GlobalWarmingPts", xml.ATTR_DOUBLE, globalWarmingPts)
			.changeAttribute("WaterConsumption", xml.ATTR_DOUBLE, waterConsumption)
			.changeAttribute("WaterScarcity", xml.ATTR_DOUBLE, waterScarcity)
			.changeAttribute("WaterScarcityPts", xml.ATTR_DOUBLE, waterScarcityPts);

		if (editHigg) {
			// remove the previous compositions 
			currentHiggMaterial.HiggMaterialCompositions.forEach((cnl) => w.deleteNode(cnl));
		}

		// create higg material composition nodes and attach to higg material node
		compositions.forEach(({ compositionNode, c8BlendPercentage, baseHiggMaterial, process }) => {
			const cn = w.createNode(null, TYPE_HIGG_MATERIAL_COMPOSITION, compositionNode.$Name)
				.changeAttribute("BlendPercentage", xml.ATTR_DOUBLE, c8BlendPercentage)
				.changeAttribute("BaseHiggMaterial", xml.ATTR_REF, baseHiggMaterial)
				.attach(higgMaterialUrl, "HiggMaterialCompositions");

			if (process) {
				cn.changeAttribute("Process", xml.ATTR_REF, process.value);
			}
		});

		actionController.m_urlNode = higgMaterialUrl;

		writeFormFields(w, higgMaterialUrl, fd.getFields(), !!fd.getNode(), Boolean, (f) => fd.getFieldValue(f));

		return submitWriter(w);
	};

	/** validate the percentage */
	protected onValidate = async (): Promise<boolean> => {
		const { cache, compositions, currentHiggMaterial, editHigg, formData, reachLimit, locale } = this;

		// check for atleast 1 selected base material
		if (!compositions.length) {
			throw new Error(locale.resource("HiggWizardSelectionEmptyError"));
		}

		//check the individual percentage
		const hasInvalidPercentage = compositions.some(({ blendPercentage }) => blendPercentage <= 0);

		if (hasInvalidPercentage) {
			throw new Error(locale.resource("HiggCompositionGuidanceSingleNotSatisfied"));
		}

		// check the percentage
		if (!reachLimit) {
			throw new Error(locale.resource("HiggCompositionGuidanceNotSatisfied"));
		}

		//  higg API call to get the scores 
		const baseMaterialIds: string[] = [];
		const blendPercentages: number[] = [];
		const processesIds: string[] = [];

		compositions.forEach((composition) => {
			const { compositionNode, blendPercentage, process } = composition;
			baseMaterialIds.push(compositionNode.Code);
			blendPercentages.push(blendPercentage);

			// pass space make sure Custom(er) Action won't get rid of this entry
			processesIds.push(process?.id || " ");
		});

		const name = formData.getFieldValue("Node Name");
		const code = formData.getFieldValue("Code");
		const categoryUrl = formData.getFieldValue("HiggCategory");
		const categoryCode = cache.get(categoryUrl).Code;
		const operation = editHigg ? "UpdateHiggMaterial" : "CreateHiggMaterial";

		const request = new PostRequest("HiggConnect", operation)
			.addParameter("Name", name)
			.addParameter("Code", code)
			.addParameter("Category", categoryCode)
			.addParameter("BaseMaterialIds", baseMaterialIds)
			.addParameter("BlendPercentages", blendPercentages)
			.addParameter("ProcessIds", processesIds);

		if (editHigg) {
			request.addParameter("CompanyCustomMaterialId", currentHiggMaterial.Id);
		}

		const scoresResponse = await request.go();
		const higgResponse = scoresResponse.data;
		const error = higgResponse.errorMessage;

		if (error) {
			// if fails, then don't proceed
			throw new Error(`${locale.resource(editHigg ? "UpdateHiggMaterial_Failed" : "NewHiggMaterial_Failed")} ${error}`);
		}

		this.higgResponse = higgResponse;

		return true;
	};

	//#endregion

	/** selecting or deselecting a higg material in the table */
	private onSelectHiggMaterial = (ctx: Context, url: string, bChecked: boolean, bOld: boolean): void => {
		const { addComposition, removeCompositions } = this;
		if (bChecked) {
			addComposition(url, ctx.cache);
		} else {
			removeCompositions(url);
		}
	};

	/** add composition */
	@action addComposition = (baseHiggMaterial: cnl, cache: ICache, isEdit: boolean = false, blendPercentage?: number, process?: ComponentItem): void => {
		const { compositionMap, currentHiggMaterial } = this;
		const key = createCompositionKey(baseHiggMaterial, currentHiggMaterial?.Id, process?.id);
		compositionMap[key] = new HiggMaterialComposition(baseHiggMaterial, cache, isEdit, key, blendPercentage, process);
	};

	/** remove composition */
	@action removeComposition = (key: string): void => {
		const { compositionMap, _compositionsHasBaseMaterial, uncheckTableRow } = this;
		const { baseHiggMaterial } = compositionMap[key];
		delete compositionMap[key];
		if (!_compositionsHasBaseMaterial(baseHiggMaterial)) {
			uncheckTableRow(baseHiggMaterial);
		}
	};

	/** remove compositions of a given base material */
	@action removeCompositions = (baseHiggMaterial: cnl): void => {
		const { compositionMap } = this;
		for (const key in compositionMap) {
			if (compositionMap[key].baseHiggMaterial === baseHiggMaterial) {
				delete compositionMap[key];
			}
		}
	};

	/** clear out the compositions */
	@action clearComposition = (): void => {
		// clear out the table selections
		const { baseHiggMaterialLibrary, uncheckTableRow } = this;
		baseHiggMaterialLibrary.forEach((url) => uncheckTableRow(url));

		// clear out the map to make the list in the right pane empty 
		this.compositionMap = {};
	};

	/** uncheck a specific table row */
	uncheckTableRow = (url: cnl): void => {
		const { formData } = this;
		const tableWidget = csiView.getView("HiggExampleMaterialLib") as TableWidget;

		// get the target context
		const ctx = tableWidget.getContext(url);

		// unselect the table row
		formData.changeSelection(ctx, false);
	};

	/** validate higg material name against higg msi */
	validateHiggMaterialName = async (name: string): Promise<boolean> => {
		const request = new PostRequest("HiggConnect", "ValidateHiggMaterialName")
			.addParameter("Name", name);

		try {
			const validateResponse = await request.go();
			const higgResponse = validateResponse.data;
			const error = higgResponse.errorMessage;

			if (error) {
				// api error
				console.error(error);
				return false;
			}

			if (higgResponse.size > 0) {
				runInAction(() => {
					this._nameViolationRes = "HiggMaterialNameNotSatisfied";
				});

				return false;
			}
		} catch (ex) {
			console.error(ex);
			return false;
		}

		runInAction(() => {
			this._nameViolationRes = "";
		});

		return true;
	};

	constructor(
		ac: ControllerInterface,
		onClose: VoidFunction,
		onSave: ControllerSaveFunction,
		locale: ILocaleProvider,
	) {
		super(2, "NewHiggMaterial", onClose, onSave, locale);
		makeObservable(this);

		this.actionController = ac;
		this.formData = ac?.getFormData();
		this.cache = ac.getCache();
	}
}

export const HiggMaterialStoreContext = createContext<HiggMaterialStore>(undefined);
export const useHiggMaterialStore = (): HiggMaterialStore => useContext(HiggMaterialStoreContext);